import {useCollaborationContext} from '@lexical/react/LexicalCollaborationContext';
import {InitialEditorStateType} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  Binding,
  createBinding,
  ExcludedProperties,
  Provider,
  SyncCursorPositionsFn,
} from '@lexical/yjs';
import {LexicalEditor} from 'lexical';
import type {JSX} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {Doc} from 'yjs';

import {CursorsContainerRef, useYjsCollaboration, useYjsFocusTracking} from './useYjsCollaboration';
import {useYjsHistory} from './useYjsHistory';

type Props = {
  id: string;
  providerFactory: (
    // eslint-disable-next-line no-shadow
    id: string,
    yjsDocMap: Map<string, Doc>
  ) => Provider;
  shouldBootstrap: boolean;
  username?: string;
  cursorColor?: string;
  cursorsContainerRef?: CursorsContainerRef;
  initialEditorState?: InitialEditorStateType;
  excludedProperties?: ExcludedProperties;
  // `awarenessData` parameter allows arbitrary data to be added to the awareness.
  awarenessData?: object;
  syncCursorPositionsFn?: SyncCursorPositionsFn;
};

type DestroyableProvider = Provider & {
  destroy?: () => void;
};

const destroyProvider = (provider: Provider) => {
  const destroy = (provider as DestroyableProvider).destroy;

  if (typeof destroy === 'function') {
    destroy.call(provider);
    return;
  }

  provider.disconnect();
};

export const CollaborationPlugin = ({
  id,
  providerFactory,
  shouldBootstrap,
  username,
  cursorColor,
  cursorsContainerRef,
  initialEditorState,
  excludedProperties,
  awarenessData,
  syncCursorPositionsFn,
}: Props): JSX.Element => {
  const collabContext = useCollaborationContext(username, cursorColor);

  const {yjsDocMap, name, color} = collabContext;

  const [editor] = useLexicalComposerContext();
  const providerFactoryRef = useRef(providerFactory);

  useEffect(() => {
    providerFactoryRef.current = providerFactory;
  }, [providerFactory]);

  useEffect(() => {
    collabContext.isCollabActive = true;

    return () => {
      // Resetting flag only when unmount top level editor collab plugin. Nested
      // editors (e.g. image caption) should unmount without affecting it
      if (editor._parentEditor == null) {
        collabContext.isCollabActive = false;
      }
    };
  }, [collabContext, editor]);

  const [providerState, setProviderState] = useState<{
    id: string;
    provider: Provider;
    doc: Doc | undefined;
  }>();

  const provider = providerState?.id === id ? providerState.provider : undefined;
  const doc = providerState?.id === id ? providerState.doc : undefined;

  const setDoc = useCallback<React.Dispatch<React.SetStateAction<Doc | undefined>>>(
    (nextDoc) => {
      setProviderState((currentProviderState) => {
        if (!currentProviderState || currentProviderState.id !== id) {
          return currentProviderState;
        }

        const resolvedDoc =
          typeof nextDoc === 'function' ? nextDoc(currentProviderState.doc) : nextDoc;

        return {
          ...currentProviderState,
          doc: resolvedDoc,
        };
      });
    },
    [id]
  );

  useEffect(() => {
    const newProvider = providerFactoryRef.current(id, yjsDocMap);
    setProviderState({
      id,
      provider: newProvider,
      doc: yjsDocMap.get(id),
    });

    return () => {
      destroyProvider(newProvider);
    };
  }, [id, yjsDocMap]);

  const currentDoc = doc || yjsDocMap.get(id);
  const [bindingState, setBindingState] = useState<{
    id: string;
    provider: Provider;
    doc: Doc;
    binding: Binding;
  }>();
  const binding =
    bindingState?.id === id && bindingState.provider === provider && bindingState.doc === currentDoc
      ? bindingState.binding
      : undefined;

  useEffect(() => {
    if (!provider || !currentDoc) {
      return;
    }

    const newBinding = createBinding(
      editor,
      provider,
      id,
      currentDoc,
      yjsDocMap,
      excludedProperties
    );
    setBindingState({
      id,
      provider,
      doc: currentDoc,
      binding: newBinding,
    });

    return () => {
      newBinding.root.destroy(newBinding);
    };
  }, [editor, provider, id, yjsDocMap, currentDoc, excludedProperties]);

  if (!provider || !binding) {
    return <></>;
  }

  return (
    <YjsCollaborationCursors
      awarenessData={awarenessData}
      binding={binding}
      color={color}
      cursorsContainerRef={cursorsContainerRef}
      editor={editor}
      id={id}
      initialEditorState={initialEditorState}
      name={name}
      provider={provider}
      setDoc={setDoc}
      shouldBootstrap={shouldBootstrap}
      yjsDocMap={yjsDocMap}
      syncCursorPositionsFn={syncCursorPositionsFn}
    />
  );
};

function YjsCollaborationCursors({
  editor,
  id,
  provider,
  yjsDocMap,
  name,
  color,
  shouldBootstrap,
  cursorsContainerRef,
  initialEditorState,
  awarenessData,
  binding,
  setDoc,
  syncCursorPositionsFn,
}: {
  editor: LexicalEditor;
  id: string;
  provider: Provider;
  yjsDocMap: Map<string, Doc>;
  name: string;
  color: string;
  shouldBootstrap: boolean;
  binding: Binding;
  setDoc: React.Dispatch<React.SetStateAction<Doc | undefined>>;
  cursorsContainerRef?: CursorsContainerRef | undefined;
  initialEditorState?: InitialEditorStateType | undefined;
  awarenessData?: object;
  syncCursorPositionsFn?: SyncCursorPositionsFn;
}) {
  const cursors = useYjsCollaboration(
    editor,
    id,
    provider,
    yjsDocMap,
    name,
    color,
    shouldBootstrap,
    binding,
    setDoc,
    cursorsContainerRef,
    initialEditorState,
    awarenessData,
    syncCursorPositionsFn
  );

  useYjsHistory(editor, binding);
  useYjsFocusTracking(editor, provider, name, color, awarenessData);

  return cursors;
}
